import { useEffect, useState, useCallback, createContext, useContext, createElement } from 'react';
import type { ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { clearDevOrg, isDevelopment } from '@/lib/subdomain';
import { useTwoFactorAuthStore, TwoFactorTriggerReason } from '@/store/twoFactorAuth';
import {
  getOrCreateSessionToken,
  getSessionId,
  getSessionToken,
  parseUserAgent,
  setSessionId,
  setStoredDeviceInfo,
  clearSessionStorage,
} from '@/lib/sessionUtils';
import { logSessionAuditEvent } from '@/lib/sessionAudit';

export interface SignInResult {
  user: User | null;
  session: Session | null;
  requires2FA?: boolean;
  requires2FASetup?: boolean;
  triggerReason?: TwoFactorTriggerReason;
}

// Only log in development mode
const isDev = import.meta.env.DEV;
const log = (...args: unknown[]) => isDev && console.log(...args);


interface Profile {
  id: string;
  organization_id: string | null;
  first_name: string | null;
  last_name: string | null;
  role: 'superadmin' | 'primary_admin' | 'owner' | 'admin' | 'manager' | 'cashier' | 'employee';
  avatar_url: string | null;
  is_active: boolean;
  designated_by?: string | null;
  designated_at?: string | null;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  business_type?:
    | 'restaurant'
    | 'qsr'
    | 'bar'
    | 'cafe'
    | 'retail'
    | 'wholesale'
    | 'other'
    | null;
  currency: string;
  timezone: string;
  cost_method?: 'average' | 'fifo' | null;
  cost_control_method?: 'default_cost' | 'last_recorded_cost' | null;
  settings?: {
    ai_upsell_enabled?: boolean;
    openai_api_key?: string;
    modules?: Record<string, { enabled?: boolean }>;
    appearance?: {
      accentColor?: string;
      theme?: string;
      font?: string;
    };
    [key: string]: unknown;
  } | null;
}

// Simple in-memory cache to avoid refetching profile/organization on every auth hook usage
const AUTH_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
let profileCache: { userId: string; data: Profile; fetchedAt: number } | null = null;
let orgCache: { orgId: string; data: Organization; fetchedAt: number } | null = null;

// Internal auth hook used only by the AuthProvider
function useAuthInternal() {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId: string): Promise<Profile | null> => {
    try {
      const now = Date.now();

      // 1) PROFILE: use cache when fresh
      const profileCacheValid =
        profileCache !== null &&
        profileCache.userId === userId &&
        now - profileCache.fetchedAt < AUTH_CACHE_TTL;

      let profileData: Profile;

      if (profileCacheValid && profileCache) {
        profileData = profileCache.data;
      } else {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (error) {
          console.error('Error fetching profile:', error);
          return null;
        }

        profileData = data as Profile;
        profileCache = { userId, data: profileData, fetchedAt: now };
      }

      setProfile(profileData);

      // 2) ORGANIZATION: only fetch if profile has one
      const orgId = (profileData as any)?.organization_id;
      if (orgId) {
        const orgCacheValid =
          orgCache !== null &&
          orgCache.orgId === orgId &&
          now - orgCache.fetchedAt < AUTH_CACHE_TTL;

        let orgData: Organization | null = null;

        if (orgCacheValid && orgCache) {
          orgData = orgCache.data;
        } else {
          const { data, error } = await supabase
            .from('organizations')
            .select('id, name, slug, logo_url, business_type, currency, timezone, settings')
            .eq('id', orgId)
            .single();

          if (error) {
            console.error('Error fetching organization:', error);
          } else if (data) {
            orgData = data as Organization;
            orgCache = { orgId, data: orgData as Organization, fetchedAt: now };
          }
        }

        if (orgData) {
          setOrganization(orgData);

          // Store org ID for login screen setting detection
          try {
            localStorage.setItem('blazepos-last-org-id', orgData.id);
          } catch {
            // Ignore localStorage errors
          }
        }
      } else {
        setOrganization(null);

        // A non-superadmin profile with no organization means the org was
        // deleted while the user's auth credentials were left intact (e.g.
        // a failed hard-delete). Force sign-out immediately so they cannot
        // reach the app in this orphaned state.
        const role = (profileData as any)?.role;
        if (role !== 'superadmin' && role !== 'primary_admin') {
          console.warn('[useAuth] Profile has no organization — forcing sign-out');
          await supabase.auth.signOut();
          profileCache = null;
          orgCache = null;
          return null;
        }
      }

      return profileData;
    } catch (err) {
      console.error('Error in fetchProfile:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Helper to clear all auth state and storage
    const clearAuthState = async () => {
      log('[useAuth] Clearing all auth state');
      try {
        await supabase.auth.signOut();
      } catch {
        // Ignore signout errors
      }
      // Clear any stale tokens from storage
      try {
        clearSessionStorage();
        // Clear Supabase's stored tokens
        const storageKeys = Object.keys(localStorage).filter(
          key => key.startsWith('sb-') || key.includes('supabase')
        );
        storageKeys.forEach(key => localStorage.removeItem(key));
      } catch {
        // Ignore storage errors
      }
      if (isMounted) {
        setSession(null);
        setUser(null);
        setProfile(null);
        setOrganization(null);
        // Clear in-memory caches so next login refetches fresh data
        profileCache = null;
        orgCache = null;
        setLoading(false);
      }
    };

    const initAuth = async (attempt = 1) => {
      const MAX_RETRIES = 3;

      try {
        // Get session - this handles token refresh automatically
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        
        if (!isMounted) return;

        if (error) {
          console.error('Error getting session:', error);
          // Auto-clear on specific auth-related errors only
          // Be precise to avoid logging users out on unrelated errors
          const errorMessage = error.message?.toLowerCase() || '';
          const isAuthError = 
            errorMessage.includes('refresh token') ||
            errorMessage.includes('invalid refresh token') ||
            errorMessage.includes('refresh token not found') ||
            errorMessage.includes('user not found') ||
            errorMessage.includes('session expired') ||
            errorMessage.includes('jwt expired') ||
            errorMessage.includes('jwt malformed') ||
            errorMessage.includes('invalid jwt') ||
            error.status === 401;
          
          if (isAuthError) {
            log('[useAuth] Session invalid, auto-clearing auth state');
            await clearAuthState();
          } else {
            // For network/transient errors, just stop loading
            setLoading(false);
          }
          return;
        }

        if (currentSession?.user) {
          setSession(currentSession);
          setUser(currentSession.user);
          await fetchProfile(currentSession.user.id);
        }
        
        setLoading(false);
      } catch (error: any) {
        // AbortError can happen if navigator.locks times out or the request
        // is cancelled (e.g. component unmount, tab switch). Retry a few
        // times since it is transient.
        if (error?.name === 'AbortError' && attempt < MAX_RETRIES && isMounted) {
          log(`[useAuth] AbortError during auth init (attempt ${attempt}/${MAX_RETRIES}), retrying...`);
          await new Promise((r) => setTimeout(r, 800 * attempt));
          if (isMounted) return initAuth(attempt + 1);
          return;
        }

        // On final AbortError failure, just finish loading without a session
        // rather than leaving the app stuck. The user can still log in manually.
        if (error?.name === 'AbortError') {
          console.warn('[useAuth] Auth init aborted after retries, continuing without session');
          if (isMounted) setLoading(false);
          return;
        }

        console.error('Error initializing auth:', error);
        const errorMessage = error?.message?.toLowerCase() || '';
        // Auto-clear on specific auth errors only
        // Be precise to avoid logging users out on unrelated errors
        if (errorMessage.includes('refresh token') || 
            errorMessage.includes('invalid refresh token') ||
            errorMessage.includes('refresh token not found') ||
            errorMessage.includes('user not found') ||
            errorMessage.includes('session expired') ||
            errorMessage.includes('jwt expired') ||
            errorMessage.includes('jwt malformed') ||
            errorMessage.includes('invalid jwt') ||
            error?.status === 401) {
          log('[useAuth] Auth error, auto-clearing auth state');
          await clearAuthState();
        } else {
          if (isMounted) setLoading(false);
        }
      }
    };

    // Set up auth state listener for subsequent changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!isMounted) return;

        log('[useAuth] Auth state change:', event);

        if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
          setProfile(null);
          setOrganization(null);
          setLoading(false);
          return;
        }

        // Handle token refresh failure - auto clear and let user re-login
        if (event === 'TOKEN_REFRESHED' && !newSession) {
          log('[useAuth] Token refresh failed, clearing auth state');
          await clearAuthState();
          return;
        }

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          if (newSession?.user) {
            setSession(newSession);
            setUser(newSession.user);
            
            // Fetch profile in background - don't block auth state
            fetchProfile(newSession.user.id).catch((error) => {
              console.error('Error fetching profile in auth change:', error);
            });
          }
          setLoading(false);
        }
      }
    );

    initAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signIn = async (emailOrUsername: string, password: string, organizationSlug?: string) => {
    log('[useAuth] signIn called', { organizationSlug });
    setLoading(true);
    try {
      let email = emailOrUsername;
      
      // If input doesn't look like an email, try to resolve it as a username
      if (!emailOrUsername.includes('@')) {
        log('[useAuth] Input looks like username, resolving...');
        const { data: userData, error: lookupError } = await supabase.rpc('get_user_email_by_username', {
          p_username: emailOrUsername,
          p_organization_slug: organizationSlug || null, // Scope to organization if provided
        });
        
        if (lookupError) {
          if (isDev) console.error('[useAuth] Username lookup error:', lookupError);
          throw new Error('Invalid username or password');
        }
        
        if (!userData || userData.length === 0) {
          throw new Error('Invalid username or password');
        }
        
        email = userData[0].email;
        log('[useAuth] Resolved username to email');
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      log('[useAuth] signIn response', { hasUser: !!data.user, hasSession: !!data.session, error });
      if (error) throw error;
      
      // Log the login action and create session in background (don't await)
      if (data.user) {
        const userId = data.user.id;
        const userEmail = data.user.email;

        // Fire and forget - don't block login
        (async () => {
          try {
            const { data: profileData } = await supabase
              .from('profiles')
              .select('role, organization_id')
              .eq('id', userId)
              .single();

            // Log audit
            await supabase.from('audit_logs').insert({
              user_id: userId,
              action: 'login',
              entity_type: 'user',
              entity_id: userId,
              is_superadmin_action: profileData?.role === 'superadmin',
              metadata: { email: userEmail },
            });
            log('Login audit logged');

            // Create session record
            if (profileData?.organization_id) {
              const sessionToken = getOrCreateSessionToken();
              const deviceInfo = parseUserAgent(navigator.userAgent);

              const { data: sessionId } = await supabase.rpc('create_user_session', {
                p_user_id: userId,
                p_organization_id: profileData.organization_id,
                p_session_token: sessionToken,
                p_user_agent: navigator.userAgent,
                p_device_type: deviceInfo.deviceType,
                p_browser: deviceInfo.browser,
                p_browser_version: deviceInfo.browserVersion,
                p_os: deviceInfo.os,
                p_os_version: deviceInfo.osVersion,
              });
              if (sessionId) {
                setSessionId(sessionId as string);
              }
              setStoredDeviceInfo({ userAgent: navigator.userAgent, ...deviceInfo });

              await logSessionAuditEvent({
                userId,
                sessionId: (sessionId as string) || getSessionId(),
                sessionToken,
                action: 'session_start',
                isSuperadmin: profileData?.role === 'superadmin',
                metadata: {
                  session_token: sessionToken,
                  device: deviceInfo,
                  user_agent: navigator.userAgent,
                },
              });
              log('Session created');
            }
          } catch (err) {
            if (isDev) console.error('Failed to log login action:', err);
          }
        })();
      }

      // Check if 2FA is required
      if (data.user) {
        const userId = data.user.id;
        const userEmail = data.user.email || '';

        // Get profile to check organization
        const { data: profileData } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('id', userId)
          .single();

        if (profileData?.organization_id) {
          const sessionToken = getOrCreateSessionToken();

          // Check 2FA requirements
          const { data: twoFactorCheck, error: twoFactorError } = await supabase.rpc('check_2fa_required', {
            p_user_id: userId,
            p_organization_id: profileData.organization_id,
            p_device_token: sessionToken,
          });

          if (!twoFactorError && twoFactorCheck?.[0]) {
            const result = twoFactorCheck[0];

            if (result.requires_2fa) {
              log('[useAuth] 2FA required:', result.trigger_reason);

              // Store pending auth in Zustand store
              // Keep the session alive - we'll verify 2FA before allowing full access
              const { setPendingAuth } = useTwoFactorAuthStore.getState();
              setPendingAuth({
                userId,
                email: userEmail,
                organizationId: profileData.organization_id,
                triggerReason: result.trigger_reason as TwoFactorTriggerReason,
                requires2FASetup: result.requires_setup || false,
              });

              // DON'T sign out - keep session alive for 2FA verification
              // The ProtectedRoute will check for pending 2FA and redirect appropriately

              setLoading(false);
              return {
                user: data.user,
                session: data.session,
                requires2FA: !result.requires_setup,
                requires2FASetup: result.requires_setup || false,
                triggerReason: result.trigger_reason as TwoFactorTriggerReason,
              };
            }
          }
        }
      }

      // Profile will be fetched by onAuthStateChange
      return { user: data.user, session: data.session };
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    metadata: { first_name: string; last_name: string; organization_name: string }
  ) => {
    // Step 1: Create the auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: metadata.first_name,
          last_name: metadata.last_name,
        },
        emailRedirectTo: `${window.location.origin}/onboarding`,
      },
    });

    if (authError) throw authError;
    if (!authData.user) throw new Error('User creation failed');

    // Check if email confirmation is required (no session returned)
    const needsEmailConfirmation = !authData.session;

    // Step 2: Atomic account setup via SECURITY DEFINER function
    // This creates org, updates profile, creates location, grants access,
    // and initializes the 14-day trial -- all in a single transaction.
    // If email confirmation is required, we still have the user object but
    // need to call the RPC. The function uses SECURITY DEFINER so it bypasses RLS.
    if (authData.session) {
      const { data: setupResult, error: setupError } = await supabase.rpc(
        'setup_new_account',
        {
          p_user_id: authData.user.id,
          p_organization_name: metadata.organization_name,
          p_first_name: metadata.first_name,
          p_last_name: metadata.last_name,
        }
      );

      if (setupError) {
        await supabase.auth.signOut();
        throw new Error(setupError.message || 'Failed to set up your account. Please try again.');
      }

      // Step 3: Notify admin system about new trial signup (fire and forget)
      try {
        const adminFunctionsUrl = import.meta.env.VITE_ADMIN_FUNCTIONS_URL;
        if (adminFunctionsUrl) {
          fetch(`${adminFunctionsUrl}/functions/v1/register-trial`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authData.session.access_token}`,
              'apikey': import.meta.env.VITE_ADMIN_ANON_KEY || '',
            },
            body: JSON.stringify({
              organization_name: metadata.organization_name,
              organization_slug: (setupResult as any)?.organization_slug,
              organization_id: (setupResult as any)?.organization_id,
              email,
              first_name: metadata.first_name,
              last_name: metadata.last_name,
            }),
          }).catch((err) => console.warn('Admin notification failed (non-blocking):', err));
        }
      } catch {
        // Non-blocking
      }
    }

    return { ...authData, needsEmailConfirmation };
  };

  const signOut = async (reason: 'manual' | 'inactivity' | 'session_timeout' | 'security' | 'unknown' = 'manual') => {
    // Log logout in background (don't await - don't block sign out)
    if (user) {
      const userId = user.id;
      const userEmail = user.email;
      const isSuperadmin = profile?.role === 'superadmin';
      const sessionToken = getSessionToken();
      const sessionId = getSessionId();

      // Fire and forget
      (async () => {
        try {
          let sessionMeta: { created_at?: string; last_active_at?: string; duration_minutes?: number } = {};
          if (sessionToken) {
            const { data: sessionRow } = await supabase
              .from('user_sessions')
              .select('created_at, last_active_at')
              .eq('session_token', sessionToken)
              .maybeSingle();
            if (sessionRow?.created_at && sessionRow?.last_active_at) {
              const durationMs =
                new Date(sessionRow.last_active_at).getTime() - new Date(sessionRow.created_at).getTime();
              sessionMeta = {
                created_at: sessionRow.created_at,
                last_active_at: sessionRow.last_active_at,
                duration_minutes: Math.max(0, Math.round(durationMs / 60000)),
              };
            } else {
              sessionMeta = sessionRow || {};
            }
          }

          await supabase.from('audit_logs').insert({
            user_id: userId,
            action: 'logout',
            entity_type: 'user',
            entity_id: userId,
            is_superadmin_action: isSuperadmin,
            metadata: {
              email: userEmail,
              reason,
              session_token: sessionToken,
              ...sessionMeta,
            },
          });

          await logSessionAuditEvent({
            userId,
            sessionId,
            sessionToken,
            action: 'session_end',
            isSuperadmin,
            metadata: {
              reason,
              session_token: sessionToken,
              ...sessionMeta,
            },
          });
        } catch (logError) {
          if (isDev) console.error('Failed to log logout action:', logError);
        }
      })();
    }

    // Cancel all queries immediately to stop any in-flight requests
    queryClient.cancelQueries();
    queryClient.clear();

    try {
      await supabase.auth.signOut();
    } catch (error) {
      // Ignore sign out errors (session may already be expired)
      console.warn('Sign out error (session may be expired):', error);
    }

    // Clear session token from storage
    try {
      clearSessionStorage();
    } catch {
      // Ignore storage errors
    }

    // Clear dev org in development mode so user returns to store selector
    if (isDevelopment()) {
      clearDevOrg();
    }

    // Always clear local state and caches even if API call fails
    setSession(null);
    setUser(null);
    setProfile(null);
    setOrganization(null);
    profileCache = null;
    orgCache = null;
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) throw new Error('No user logged in');

    const { data, error } = await (supabase
      .from('profiles') as any)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', user.id)
      .select()
      .single();

    if (error) throw error;
    setProfile(data as Profile);
    return data;
  };

  const updateOrganization = async (updates: Partial<Organization> & Record<string, any>) => {
    if (!organization?.id) throw new Error('No organization found');

    const { data, error } = await (supabase
      .from('organizations') as any)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', organization.id)
      .select()
      .single();

    if (error) {
      // If the error is about no rows returned (RLS issue with RETURNING),
      // try the update without select and merge state manually
      if (error.code === 'PGRST116') {
        const { error: updateError } = await (supabase
          .from('organizations') as any)
          .update({
            ...updates,
            updated_at: new Date().toISOString(),
          })
          .eq('id', organization.id);

        if (updateError) throw updateError;

        // Merge updates into current organization state
        const updatedOrg = { ...organization, ...updates } as Organization;
        setOrganization(updatedOrg);
        // Also update the cache
        orgCache = { orgId: organization.id, data: updatedOrg, fetchedAt: Date.now() };
        return updatedOrg;
      }
      throw error;
    }

    setOrganization(data as Organization);
    // Update cache with fresh data
    orgCache = { orgId: organization.id, data: data as Organization, fetchedAt: Date.now() };
    return data;
  };

  const refetchProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  // Compute role-based booleans only when profile is loaded
  const role = profile?.role;

  return {
    user,
    session,
    profile,
    organization,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    updateOrganization,
    refetchProfile,
    isAuthenticated: !!user && !!profile,
    isSuperadmin: role === 'superadmin',
    isPrimaryAdmin: role === 'primary_admin',
    isBusinessOwner: role === 'superadmin' || role === 'primary_admin', // Business owner level access
    isOwner: role === 'superadmin' || role === 'primary_admin' || role === 'owner',
    isAdmin: role === 'superadmin' || role === 'primary_admin' || role === 'owner' || role === 'admin',
    isManager: role === 'superadmin' || role === 'primary_admin' || role === 'owner' || role === 'admin' || role === 'manager',
  };
}

// Context + Provider so we only run auth logic once at the app root
type AuthContextValue = ReturnType<typeof useAuthInternal>;
const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const value = useAuthInternal();
  return createElement(AuthContext.Provider, { value }, children);
}

// Public hook consumed by the rest of the app
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
}
